import {
	ChangeDetectionStrategy,
	Component,
	output,
	OnInit,
	Signal,
	WritableSignal,
	computed,
	input,
	signal
} from '@angular/core';
import { FoodDb } from 'lib/cosmos/food/foodDb.js';
import { Observable, map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

import { FormSaveHandler } from 'lib/ui/form/form-save-button.component.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { IFileFormFieldCropOptions } from 'lib/ui/form/fields/file-form-field.component.js';

interface INutritionField {
	required: boolean;
	title: string;
	maxValue: number;
	minValue: number;
	valueAsSignal: WritableSignal<number | undefined>;
}

@Component({
    selector: 'mod-food-form',
    templateUrl: './food-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FoodFormComponent extends TypeCoreComponent<FoodDb.IFoodItem | undefined> implements OnInit {
	public readonly nutritionMinValue: number = 0;
	public readonly nutritionMaxValue: number = 9999;

	public readonly foodImageCrop: IFileFormFieldCropOptions = {
		aspectRatio: 'square',
		resizeToWidth: 1024
	};

	public readonly isInDialog = input<boolean>(false);

	public readonly create = output<FoodDb.IFoodItem>();

	public readonly form = {
		id: signal<string | undefined>(undefined),
		name: signal<string | undefined>(undefined),
		notes: signal<string | undefined>(undefined),
		images: signal<CosmosDb.ICosmosFileReference[]>([]),
		proteins: signal<number | undefined>(undefined),
		fats: signal<number | undefined>(undefined),
		carbs: signal<number | undefined>(undefined),
		fiber: signal<number | undefined>(undefined),
		water: signal<number | undefined>(undefined),
		sugars: signal<number | undefined>(undefined),
		kcal: signal<number | undefined>(undefined),
		sodium: signal<number | undefined>(undefined),
		starch: signal<number | undefined>(undefined)
	};

	public readonly nutritionFields = computed<INutritionField[]>(() => {
		const fields: INutritionField[] = [
			{
				title: 'Kcal',
				required: false,
				valueAsSignal: this.form.kcal,
				maxValue: this.nutritionMaxValue,
				minValue: this.nutritionMinValue
			},
			{
				title: 'Proteins',
				required: false,
				valueAsSignal: this.form.proteins,
				maxValue: this.nutritionMaxValue,
				minValue: this.nutritionMinValue
			},
			{
				title: 'Fats',
				required: false,
				valueAsSignal: this.form.fats,
				maxValue: this.nutritionMaxValue,
				minValue: this.nutritionMinValue
			},
			{
				title: 'Carbs',
				required: false,
				valueAsSignal: this.form.carbs,
				maxValue: this.nutritionMaxValue,
				minValue: this.nutritionMinValue
			},
			{
				title: 'Sugars',
				required: false,
				valueAsSignal: this.form.sugars,
				maxValue: this.nutritionMaxValue,
				minValue: this.nutritionMinValue
			},
			{
				title: 'Sodium',
				required: false,
				valueAsSignal: this.form.sodium,
				maxValue: this.nutritionMaxValue,
				minValue: this.nutritionMinValue
			},
			{
				title: 'Fiber',
				required: false,
				valueAsSignal: this.form.fiber,
				maxValue: this.nutritionMaxValue,
				minValue: this.nutritionMinValue
			},
			{
				title: 'Starch',
				required: false,
				valueAsSignal: this.form.starch,
				maxValue: this.nutritionMaxValue,
				minValue: this.nutritionMinValue
			},
			{
				title: 'Water',
				required: false,
				valueAsSignal: this.form.water,
				maxValue: this.nutritionMaxValue,
				minValue: this.nutritionMinValue
			}
		];

		return fields;
	});

	public readonly isEditForm: Signal<boolean> = computed(() => {
		if (this.form.id()) {
			return true;
		}
		return false;
	});

	public readonly saveHandler = computed<FormSaveHandler>(() => {
		const sharedData = this.sharedData();
		const form = this.form;

		const name = form.name();

		if (!name) {
			return undefined;
		}

		const nutrients: FoodDb.IFoodNutrients = {
			carbs: form.carbs() ?? 0,
			fats: form.fats() ?? 0,
			fiber: form.fiber() ?? 0,
			kcal: form.kcal() ?? 0,
			proteins: form.proteins() ?? 0,
			sodium: form.sodium() ?? 0,
			starch: form.starch() ?? 0,
			sugars: form.sugars() ?? 0,
			water: form.water() ?? 0
		};

		return this.dependencies.httpService
			.upsert<FoodDb.IFoodItem, FoodDb.IBaseFoodItem>({
				type: 'food',
				existingItem: sharedData?.data,
				createModel: {
					name: name,
					source: 'custom',
					notes: form.notes(),
					images: form.images(),
					nutrients: nutrients
				},
				patchModel: (item) => {
					return {
						id: item.id,
						name: name,
						notes: form.notes(),
						images: form.images(),
						nutrients: nutrients
					};
				}
			})
			.pipe(
				map((response) => {
					super.notifyUpsert('Food', this.isEditForm());
					this.create.emit(response.data);

					super.updateSharedData({
						data: response.data,
						linkedItems: response.linkedItems
					});

					if (!this.isInDialog() && !this.isEditForm()) {
						this.dependencies.navigateService
							.actionRoute((routes) => routes.foodRoutes.food.children.edit, response.data.id)
							.navigate();
					}
				})
			);
	});

	public readonly uploadFunction: (file: File) => Observable<CosmosDb.ICosmosFileReference[]> = (file) => {
		return this.dependencies.httpService
			.uploadFile({
				file: file,
				type: 'food'
			})
			.pipe(
				map((response) => {
					return [...this.form.images(), response.data];
				})
			);
	};

	constructor() {
		super();

		this.sharedDataInit = (sharedData) => {
			if (sharedData && sharedData.data) {
				this.form.id.set(sharedData.data.id);
				this.form.name.set(sharedData.data.system.name);
				this.form.notes.set(sharedData.data.notes);
				this.form.images.set(sharedData.data.images ?? []);

				if (sharedData.data.nutrients) {
					this.form.proteins.set(sharedData.data.nutrients.proteins);
					this.form.carbs.set(sharedData.data.nutrients.carbs);
					this.form.fats.set(sharedData.data.nutrients.fats);
					this.form.sugars.set(sharedData.data.nutrients.sugars);
					this.form.fiber.set(sharedData.data.nutrients.fiber);
					this.form.kcal.set(sharedData.data.nutrients.kcal);
					this.form.sodium.set(sharedData.data.nutrients.sodium);
					this.form.water.set(sharedData.data.nutrients.water);
					this.form.starch.set(sharedData.data.nutrients.starch);
				}
			}
		};
	}

	handleUploadImage(files: CosmosDb.ICosmosFileReference[] | undefined): void {
		if (files) {
			this.form.images.set(files);
			const saveHandler = this.saveHandler();

			if (saveHandler && this.isEditForm()) {
				super.subscribeToObservable(saveHandler);
			}
		}
	}
}
